import {dataTestId} from '@hconnect/uikit'
import {Loader, CardBox} from '@hconnect/uikit/src/lib2'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft'
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight'
import SensorsIcon from '@mui/icons-material/Sensors'
import ZoomInMapIcon from '@mui/icons-material/ZoomInMap'
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap'
import {
  Box,
  Stack,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Button,
  Typography,
  useMediaQuery,
  Theme,
  Switch,
  FormControlLabel
} from '@mui/material'
import {useEffect, useRef, useState} from 'react'
import {NodeApi, Tree, TreeApi} from 'react-arborist'
import {useTranslation} from 'react-i18next'
import useResizeObserver from 'use-resize-observer'

import {SignalTreeViews} from '../../../shared/types/plantMonitoring.types'
import {UserSignalGroupsConfig} from '../../../shared/types/sensor.types'
import {usePlantMonitoring, usePlantMonitoringSelectors} from '../../context/PlantMonitoringContext'
import {useSignalMetadata} from '../../context/SignalMetadataContext'
import {SignalTelemetryProvider} from '../../context/SignalTelemetryContext'
import {useSignalDescription} from '../../hooks/useSignalDescription'
import {useSignalTreeData} from '../../hooks/useSignalTreeData'

import {Groups} from './Groups/Groups'
import {SignalTreeFilters} from './SignalTreeFilters'
import {Node, NodeSignalData, TreeNode} from './SignalTreeNode'

type SignalTreeProps = {
  forceEquipmentOnly?: boolean
  onSignalHover?: (data: NodeSignalData | null) => void
  groupsConfig: UserSignalGroupsConfig
}

export const SignalTreeContainer = ({
  forceEquipmentOnly,
  onSignalHover,
  groupsConfig
}: SignalTreeProps) => {
  const {t} = useTranslation()
  const {actions, state} = usePlantMonitoring()
  const {localNames} = useSignalMetadata()

  const isLgDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'))

  if (state.uiLayout.signalTreeCollapsed && !isLgDown) {
    return (
      <Button
        variant="text"
        onClick={() => actions.collapseSignalTree()}
        startIcon={<KeyboardDoubleArrowRightIcon />}
        sx={{
          width: 48,
          minWidth: 48,
          maxWidth: 48,
          padding: 0,
          height: '100%',
          color: 'common.white',
          '&:hover': {backgroundColor: '#014A94'},
          '& .MuiSvgIcon-root': {
            ml: 2
          }
        }}
      ></Button>
    )
  }

  return (
    <Stack spacing={1} useFlexGap>
      <CardBox px={0} display="flex" flexDirection="column" gap={2} pt={3}>
        <Stack direction="column" justifyContent="space-between" alignItems={'left'} gap={2} px={3}>
          <Box display="flex">
            <ToggleButtonGroup
              color="primary"
              value={state.signalTreeView}
              exclusive
              onChange={(_, value) => {
                typeof value === 'string' && actions.setSignalTreeView(value as SignalTreeViews)
              }}
              sx={{backgroundColor: '#F0F2F5', border: 'none !important', flexGrow: 1}}
            >
              <ToggleButton
                value="signals"
                sx={{flexGrow: 1, border: 'none !important', display: 'flex', gap: 1}}
              >
                <SensorsIcon />
                {t('signalTree.signalView')}
              </ToggleButton>
              <ToggleButton
                value="groups"
                sx={{flexGrow: 1, border: 'none !important', display: 'flex', gap: 1}}
              >
                <FolderOutlinedIcon />
                {t('signalTree.groupView')}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          {state.signalTreeView === SignalTreeViews.SIGNALS && <SignalTreeFilters />}
        </Stack>
        <SignalTelemetryProvider localNames={localNames}>
          {state.signalTreeView === SignalTreeViews.SIGNALS && (
            <SignalTree forceEquipmentOnly={forceEquipmentOnly} onSignalHover={onSignalHover} />
          )}
          {state.signalTreeView === SignalTreeViews.GROUPS && <Groups config={groupsConfig} />}
        </SignalTelemetryProvider>
      </CardBox>
      <Button
        variant="text"
        color="secondary"
        onClick={() => actions.collapseSignalTree()}
        sx={{
          display: isLgDown ? 'none' : 'flex',
          color: 'common.white',
          '&:hover': {backgroundColor: '#014A94'}
        }}
        startIcon={<KeyboardDoubleArrowLeftIcon />}
      >
        {t('signalTree.collapsePanel')}
      </Button>
    </Stack>
  )
}

const SignalTree = ({forceEquipmentOnly, onSignalHover}: Omit<SignalTreeProps, 'groupsConfig'>) => {
  const {t} = useTranslation()
  const {isPending: signalsPending} = useSignalMetadata()
  const [search, setSearch] = useState<string>('')
  const [equipmentOnly, setEquipmentOnly] = useState<boolean>(!!forceEquipmentOnly)
  const {actions, state} = usePlantMonitoring()
  const {selectedIds, isSignalSelected} = usePlantMonitoringSelectors()
  const [isInitial, setIsInitial] = useState<boolean>(true)
  const treeData = useSignalTreeData({equipmentOnly, ...state.filters})
  const {ref: treeContainerRef, width, height} = useResizeObserver()
  const treeRef = useRef<TreeApi<TreeNode>>()
  const getSignalDescription = useSignalDescription()

  const handleNodesSelected = (nodes: NodeApi<TreeNode>[]) => {
    // TODO! when node is collapsed all its children are not send anymore in the onSelect
    const newSelectedIds = nodes
      .map((node) => node.data.signalData?.localName)
      .filter((id): id is string => !!id)

    if (newSelectedIds.length === selectedIds.length) return
    if (isInitial && newSelectedIds.length === 0) {
      setIsInitial(false)
      return
    }

    const signalsToBeAdded = newSelectedIds.filter((id) => !isSignalSelected(id))
    const signalsToBeRemoved = newSelectedIds.length
      ? selectedIds.filter((id) => !newSelectedIds.includes(id))
      : selectedIds

    if (signalsToBeAdded.length) {
      actions.selectSignals(signalsToBeAdded)
    }
    if (signalsToBeRemoved.length) {
      actions.deselectSignals(signalsToBeRemoved)
    }
  }

  const handleSearchMatch = (node: NodeApi<TreeNode>) => {
    const normalizedSearch = search.toLowerCase().trim()
    const label = node.data.label.toLowerCase()
    const description = getSignalDescription({
      name: node.data.signalData?.name,
      description: node.data.signalData?.description
    }).toLowerCase()

    if (normalizedSearch === '') return true
    return label.includes(normalizedSearch) || description.includes(normalizedSearch)
  }

  // sync url and tree selection, currently react-arborist does not support controlled selection properly
  useEffect(() => {
    if (signalsPending) return
    const tree = treeRef.current
    const treeSignalIds = tree?.selectedNodes
      .map((node) => node.data.signalData?.localName)
      .filter((name): name is string => !!name)

    const selectionIsSame =
      selectedIds.length === treeSignalIds?.length &&
      selectedIds.every((id) => treeSignalIds?.includes(id))

    if (selectionIsSame) return
    tree?.setSelection({
      ids: selectedIds,
      anchor: null,
      mostRecent: null
    })
  }, [signalsPending, selectedIds])

  if (signalsPending) {
    return <Loader />
  }

  return (
    <>
      <Box>
        <Stack
          direction="column"
          justifyContent="space-between"
          {...dataTestId('janus-plant-structure-menu')}
          alignItems={'left'}
          gap={2}
        >
          <Box px={3} display="flex" flexDirection="column" gap={1}>
            <TextField
              label={t('general.search')}
              fullWidth
              variant="filled"
              defaultValue={search}
              onChange={(e) => setSearch(e.target.value)}
              {...dataTestId('plant-structure-text-filter')}
              sx={{
                '& .MuiFilledInput-root': {
                  backgroundColor: 'transparent',
                  borderRadius: '6px',
                  '&:hover': {
                    backgroundColor: 'transparent'
                  },
                  '&.Mui-focused': {
                    backgroundColor: 'transparent'
                  }
                }
              }}
            />
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              flexDirection={{xs: 'column', sm: 'row'}}
              gap={1}
            >
              {!forceEquipmentOnly && (
                <Button
                  onClick={() => setEquipmentOnly((prev) => !prev)}
                  size="small"
                  variant="text"
                  startIcon={equipmentOnly ? <ZoomOutMapIcon /> : <ZoomInMapIcon />}
                  sx={{alignSelf: 'flex-start'}}
                >
                  {equipmentOnly ? t('signalTree.showAll') : t('signalTree.equipmentOnly')}
                </Button>
              )}
              <FormControlLabel
                control={
                  <Switch
                    checked={state.uiLayout.englishDescription}
                    onChange={actions.toggleEnglishDescription}
                  />
                }
                sx={{alignSelf: 'flex-start'}}
                label={
                  <Typography fontSize={14} fontWeight={500} color="primary">
                    English descriptions
                  </Typography>
                }
              />
            </Box>
          </Box>
          <Box ref={treeContainerRef} height={'55vh'}>
            {treeData.length === 0 && (
              <Typography textAlign="center" variant="subtitle1">
                {t('label.noData')}
              </Typography>
            )}
            <Tree
              ref={treeRef}
              data={treeData}
              openByDefault={true}
              width={width}
              rowHeight={60}
              height={height}
              indent={14}
              searchTerm={search}
              searchMatch={handleSearchMatch}
              onSelect={(nodes) => handleNodesSelected(nodes)}
            >
              {(props) => <Node {...props} onHover={onSignalHover} />}
            </Tree>
          </Box>
        </Stack>
      </Box>
    </>
  )
}
